import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, FileImage, Send, Paperclip } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ExportMenu from "@/components/ExportMenu";
import ReviewAttachmentsDialog from "@/components/ReviewAttachmentsDialog";
import { toast } from "sonner";

export default function Reviews() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [month, setMonth] = useState("");
  const [reviewType, setReviewType] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [filesRow, setFilesRow] = useState(null);
  const canEdit = ["crm_manager", "process_manager", "branch_manager"].includes(user?.role);

  useEffect(() => {
    api.get("/clients", { params: { page_size: 500 } })
      .then((r) => setClients(r.data.items))
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          client_id: clientId || undefined,
          month: month || undefined,
          review_type: reviewType || undefined,
        };
        const { data } = await api.get("/reviews", { params });
        setRows(data);
      } finally { setLoading(false); }
    })();
  }, [clientId, month, reviewType]);

  const filtered = useMemo(
    () => rows.filter((r) => !q || (r.client_name || "").toLowerCase().includes(q.toLowerCase())),
    [rows, q],
  );

  const downloadMom = async (review) => {
    try {
      const t = localStorage.getItem("mptg_token");
      const res = await fetch(
        `${API}/reviews/${review.id}/export?fmt=pdf`,
        { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} },
      );
      if (!res.ok) throw new Error(`MOM download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MOM_${(review.client_name || "client").replace(/\s+/g, "_")}_${review.review_type}_${review.period_month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("MOM downloaded");
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const sendMom = async (review) => {
    if (!window.confirm(`Email the MOM PDF to all attendees of "${review.client_name}" — ${review.review_type} ${review.period_month}?`)) return;
    try {
      const { data } = await api.post(`/reviews/${review.id}/send-mom`, { extra_recipients: [] });
      if (data.skipped) {
        toast.warning(data.message || "No recipients");
        return;
      }
      const status = data.smtp_enabled
        ? `${data.sent} sent, ${data.failed} failed`
        : `${data.sent + data.failed} queued (configure SMTP in Settings to deliver)`;
      toast.success(`MOM dispatched — ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="reviews-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="mptg-overline">Governance</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Business Reviews</h1>
          <p className="text-slate-500 mt-1">{rows.length} review{rows.length === 1 ? "" : "s"} matching</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu
            endpoint="/reviews/export/list"
            params={{ client_id: clientId, month, review_type: reviewType }}
            filename={`reviews_${month || "all"}`}
            label="Export list"
            testid="reviews-export"
          />
          {canEdit && (
            <Button asChild className="bg-[#2E86C1] hover:bg-[#2471A3] text-white">
              <Link to="/reviews/new" data-testid="new-review-btn"><Plus className="w-4 h-4 mr-1" /> New review</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="mptg-card flex flex-wrap items-center gap-3" data-testid="reviews-filters">
        <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64" data-testid="reviews-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="reviews-month-filter" />
        <Select value={reviewType || "all"} onValueChange={(v) => setReviewType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="reviews-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="WBR">WBR</SelectItem>
            <SelectItem value="MBR">MBR</SelectItem>
            <SelectItem value="QBR">QBR</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Search client name…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="review-search" />
        {(clientId || month || reviewType || q) && (
          <button onClick={() => { setClientId(""); setMonth(""); setReviewType(""); setQ(""); }}
            className="text-xs text-[#2E86C1] hover:underline" data-testid="reviews-clear-filters">Clear</button>
        )}
      </div>

      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead><tr><th>Type</th><th>Client</th><th>Period</th><th>Date</th><th>NPS</th><th>CSAT</th><th>Open actions</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="py-8 text-center text-slate-400">Loading…</td></tr> :
             filtered.length === 0 ? <tr><td colSpan={8} className="py-8 text-center text-slate-400">No reviews match.</td></tr> :
              filtered.map((r) => (
                <tr key={r.id} data-testid={`review-row-${r.id}`}>
                  <td><span className={`rounded px-2 py-0.5 text-xs font-semibold ${r.review_type === "QBR" ? "badge-blue" : r.review_type === "MBR" ? "badge-slate" : "badge-green"}`}>{r.review_type}</span></td>
                  <td><Link to={`/clients/${r.client_id}`} className="text-[#2E86C1] hover:underline">{r.client_name}</Link></td>
                  <td>{r.period_month}</td>
                  <td>{r.review_date}</td>
                  <td className="tabular-nums">{r.nps_score ?? "-"}</td>
                  <td className="tabular-nums">{r.csat_score ?? "-"}</td>
                  <td className="tabular-nums">{r.open_actions}</td>
                  <td className="text-right whitespace-nowrap space-x-3">
                    <button onClick={() => downloadMom(r)} className="text-xs text-[#1E8449] hover:underline inline-flex items-center gap-1" data-testid={`mom-pdf-${r.id}`}>
                      <FileImage className="w-3.5 h-3.5" /> MOM PDF
                    </button>
                    {canEdit && (
                      <button onClick={() => sendMom(r)} className="text-xs text-[#2E86C1] hover:underline inline-flex items-center gap-1" data-testid={`send-mom-${r.id}`}>
                        <Send className="w-3.5 h-3.5" /> Send
                      </button>
                    )}
                    <button onClick={() => setFilesRow(r)} className="text-xs text-[#2E86C1] hover:underline inline-flex items-center gap-1" data-testid={`files-${r.id}`}>
                      <Paperclip className="w-3.5 h-3.5" />
                      Files{(r.attachments || []).length > 0 ? ` (${r.attachments.length})` : ""}
                    </button>
                    <Link to={`/reviews/${r.id}`} className="text-xs text-[#2E86C1] hover:underline" data-testid={`open-review-${r.id}`}>Open →</Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {filesRow && (
        <ReviewAttachmentsDialog
          review={filesRow}
          canEdit={canEdit}
          onClose={() => { setFilesRow(null); load(); }}
        />
      )}
    </div>
  );
}
